import React, { useState, useRef, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link,
  Image,
  Undo,
  Redo,
  Plus,
  ChevronDown,
  Star,
  MousePointerClick,
  GalleryHorizontal,
  FileText,
  Workflow,
  MessageSquare,
  Minus,
  AlignCenter,
  ImageIcon,
  Code2,
  Eye,
  Pencil,
  Table2,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

type DialogKey =
  | 'starRating'
  | 'cta'
  | 'logo'
  | 'mediaCarousel'
  | 'itemCarousel'
  | 'form'
  | 'singleForm'
  | 'interactiveFlow'
  | 'googleReviews'
  | 'table';

export interface EditorToolbarProps {
  editor: Editor | null;
  onOpenDialog: (key: DialogKey) => void;
  onInsertRaw: (raw: string) => void;
  onInsertAsset?: () => void;
  isRawMode: boolean;
  onToggleRawMode: () => void;
  isPreviewMode: boolean;
  onTogglePreview: () => void;
  /** When true, all formatting/insert buttons are disabled. The Raw/Preview toggle still works. */
  readOnly?: boolean;
  /** Ref to the raw textarea — needed for reading selection in raw mode */
  rawTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Current raw value */
  rawValue: string;
  /** Callback to update raw value */
  onRawChange: (newVal: string) => void;
  /** Cursor (selectionStart) in the raw textarea (for active-state detection) */
  rawCursorPos?: number;
  /** selectionEnd in the raw textarea */
  rawSelEnd?: number;
  /** Raw-mode undo/redo callbacks */
  onRawUndo?: () => void;
  onRawRedo?: () => void;
  canRawUndo?: boolean;
  canRawRedo?: boolean;
  /**
   * 'full' (default) — all toolbar buttons visible.
   * 'minimal' — shows only Bold/Italic/Strike, Headings, Link and Raw/Preview toggle.
   *             Hides Undo/Redo, Lists, Blockquote, Code, and the Insert dropdown.
   * 'basic'   — text formatting only: hides Image and the Insert dropdown.
   */
  toolbarVariant?: 'full' | 'minimal' | 'basic';
}

// ─── Sub-components ─────────────────────────────────────────────────────────

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  isActive = false,
  disabled = false,
  title,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5  hover:bg-gray-200 transition-colors ${isActive ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
);

const ToolbarDivider = () => <div className="w-px h-6 bg-gray-300 mx-1" />;

// ─── Raw-mode active-state helpers ──────────────────────────────────────────

/**
 * Returns true when `cursorInLine` falls inside an inline marker pair on the
 * current line (e.g. `**bold**`, `*italic*`, `~~strike~~`).
 */
function isInsideInlineMarker(lineText: string, cursorInLine: number, marker: string): boolean {
  const parts = lineText.split(marker);
  if (parts.length < 2) return false;
  let charCount = 0;
  for (let i = 0; i < parts.length; i++) {
    charCount += parts[i].length;
    if (cursorInLine <= charCount) return i % 2 === 1;
    charCount += marker.length;
  }
  return false;
}

interface RawActiveStates {
  bold: boolean; italic: boolean; strike: boolean;
  h1: boolean; h2: boolean; h3: boolean;
  bulletList: boolean; orderedList: boolean;
  blockquote: boolean; codeBlock: boolean;
}

/**
 * Count consecutive `*` going backwards from `pos-1` in `text`.
 */
function countStarsBefore(text: string, pos: number): number {
  let n = 0;
  for (let i = pos - 1; i >= 0 && text[i] === '*'; i--) n++;
  return n;
}
/**
 * Count consecutive `*` going forwards from `pos` in `text`.
 */
function countStarsAfter(text: string, pos: number): number {
  let n = 0;
  for (let i = pos; i < text.length && text[i] === '*'; i++) n++;
  return n;
}

function computeRawActiveStates(value: string, selStart: number, selEnd: number): RawActiveStates {
  const lineStart = value.lastIndexOf('\n', selStart - 1) + 1;
  const lineEndIdx = value.indexOf('\n', selStart);
  const lineEnd = lineEndIdx === -1 ? value.length : lineEndIdx;
  const line = value.substring(lineStart, lineEnd);
  const cursorInLine = selStart - lineStart;

  // Block-level (check most-specific first to avoid false positives)
  const h3 = /^### /.test(line);
  const h2 = !h3 && /^## /.test(line);
  const h1 = !h2 && !h3 && /^# /.test(line);
  const bulletList = /^[-*+] /.test(line);
  const orderedList = /^\d+\. /.test(line);
  const blockquote = /^> /.test(line);
  const codeBlock = (((value.substring(0, selStart).match(/```/g) || []).length) % 2) === 1;

  // ── Asterisk-based marks (bold / italic) ─────────────────────────────────
  let bold = false;
  let italic = false;
  const hasSelection = selEnd > selStart;

  if (!hasSelection) {
    // Cursor only: use segment-counting helpers.
    // For bold (**): cursor inside **...**
    bold = isInsideInlineMarker(line, cursorInLine, '**');
    // For italic (*): replace ** with a 2-char placeholder so they don't count as *
    const lineForItalic = line.replace(/\*\*/g, '\x01\x01');
    italic = isInsideInlineMarker(lineForItalic, cursorInLine, '*');
  } else {
    // Selection: count consecutive * at selection boundaries.
    // "Inner stars": the selection itself begins/ends with * (e.g. user selected **bold**)
    const sel = value.substring(selStart, selEnd);
    let leadStars = 0;
    while (leadStars < sel.length && sel[leadStars] === '*') leadStars++;
    let trailStars = 0;
    while (trailStars < sel.length && sel[sel.length - 1 - trailStars] === '*') trailStars++;
    const innerStars = Math.min(leadStars, trailStars);

    // "Outer stars": * markers sitting just outside the selection (text-only selection)
    const outerBefore = countStarsBefore(value, selStart);
    const outerAfter = countStarsAfter(value, selEnd);
    const outerStars = Math.min(outerBefore, outerAfter);

    const effectiveStars = Math.max(innerStars, outerStars);
    bold = effectiveStars >= 2;
    italic = effectiveStars === 1 || effectiveStars >= 3;
  }

  // ── Strikethrough (~~) — same 4-case logic, no * ambiguity ───────────────
  const checkStrike = (): boolean => {
    const mLen = 2;
    const selEndInLine = Math.min(selEnd, lineEnd) - lineStart;
    if (isInsideInlineMarker(line, cursorInLine, '~~')) return true;
    if (hasSelection && isInsideInlineMarker(line, selEndInLine, '~~')) return true;
    if (selStart >= mLen &&
      value.substring(selStart - mLen, selStart) === '~~' &&
      value.substring(selEnd, selEnd + mLen) === '~~') return true;
    const sel = value.substring(selStart, selEnd);
    if (sel.length >= mLen * 2 && sel.startsWith('~~') && sel.endsWith('~~')) return true;
    return false;
  };
  const strike = checkStrike();

  return { bold, italic, strike, h1, h2, h3, bulletList, orderedList, blockquote, codeBlock };
}

// ─── Raw-mode markdown helpers ──────────────────────────────────────────────

/**
 * Smart asterisk toggle for bold (**) and italic (*).
 *
 * Counts all consecutive `*` at both sides of the selection (inside or outside),
 * derives the current effective star count, then adds or removes `markerCount`
 * stars so that bold and italic can coexist as `***text***` without interfering.
 */
function rawToggleAsteriskWrap(
  textarea: HTMLTextAreaElement,
  value: string,
  onChange: (v: string) => void,
  markerCount: number, // 1 for italic, 2 for bold
  placeholder = 'text'
) {
  const selStart = textarea.selectionStart;
  const selEnd = textarea.selectionEnd;

  // Stars outside the selection
  const outerLeft = countStarsBefore(value, selStart);
  const outerRight = countStarsAfter(value, selEnd);

  // Stars inside the selection (at leading/trailing edges)
  const sel = value.substring(selStart, selEnd);
  let innerLeft = 0;
  while (innerLeft < sel.length && sel[innerLeft] === '*') innerLeft++;
  let innerRight = 0;
  while (innerRight < sel.length && sel[sel.length - 1 - innerRight] === '*') innerRight++;
  const innerStars = Math.min(innerLeft, innerRight);

  let zoneStart: number;
  let zoneEnd: number;
  let core: string;
  let currentStars: number;

  if (innerStars > 0) {
    // Selection includes the markers (e.g. user selected "**bold**")
    currentStars = innerStars;
    core = sel.slice(innerStars, sel.length - innerStars);
    zoneStart = selStart;
    zoneEnd = selEnd;
  } else if (outerLeft > 0 || outerRight > 0) {
    // Markers sit outside the selection (text-only selection)
    currentStars = Math.min(outerLeft, outerRight);
    core = sel || placeholder;
    zoneStart = selStart - currentStars;
    zoneEnd = selEnd + currentStars;
  } else {
    // No stars anywhere — just wrap
    currentStars = 0;
    core = sel || placeholder;
    zoneStart = selStart;
    zoneEnd = selEnd;
  }

  // Is the mark currently active?
  const isActive = markerCount === 1
    ? (currentStars === 1 || currentStars >= 3)  // italic active
    : (currentStars >= 2);                        // bold active

  const targetStars = Math.max(0, isActive ? currentStars - markerCount : currentStars + markerCount);
  const marker = '*'.repeat(targetStars);
  const newZoneContent = marker + core + marker;
  const newValue = value.substring(0, zoneStart) + newZoneContent + value.substring(zoneEnd);

  onChange(newValue);
  requestAnimationFrame(() => {
    textarea.selectionStart = zoneStart + targetStars;
    textarea.selectionEnd = zoneStart + targetStars + core.length;
    textarea.focus();
  });
}

/**
 * Toggle wrap: if selection is already wrapped with prefix/suffix, remove them.
 * Otherwise, wrap the selection (or insert placeholder if nothing selected).
 * Handles nested styles (e.g. bold inside italic) by checking exact boundaries.
 */
function rawWrapSelection(
  textarea: HTMLTextAreaElement,
  value: string,
  onChange: (v: string) => void,
  prefix: string,
  suffix: string,
  placeholder = 'text'
) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const pLen = prefix.length;
  const sLen = suffix.length;

  // Case 1: Check if the text OUTSIDE the selection wraps it
  const beforeChars = value.substring(start - pLen, start);
  const afterChars = value.substring(end, end + sLen);

  if (start >= pLen && beforeChars === prefix && afterChars === suffix) {
    // Remove the wrapping markers from outside
    const newValue = value.substring(0, start - pLen) + value.substring(start, end) + value.substring(end + sLen);
    onChange(newValue);
    requestAnimationFrame(() => {
      textarea.selectionStart = start - pLen;
      textarea.selectionEnd = end - pLen;
      textarea.focus();
    });
    return;
  }

  // Case 2: Check if the selection itself includes the markers
  const selected = value.substring(start, end);
  if (selected.length >= pLen + sLen && selected.startsWith(prefix) && selected.endsWith(suffix)) {
    // Remove markers from inside selection
    const inner = selected.slice(pLen, -sLen);
    const newValue = value.substring(0, start) + inner + value.substring(end);
    onChange(newValue);
    requestAnimationFrame(() => {
      textarea.selectionStart = start;
      textarea.selectionEnd = start + inner.length;
      textarea.focus();
    });
    return;
  }

  // Case 3: Not wrapped — add the wrapping
  const textToWrap = selected || placeholder;
  const newValue = value.substring(0, start) + prefix + textToWrap + suffix + value.substring(end);
  onChange(newValue);
  requestAnimationFrame(() => {
    textarea.selectionStart = start + pLen;
    textarea.selectionEnd = start + pLen + textToWrap.length;
    textarea.focus();
  });
}

/**
 * Toggle line prefix: if all selected lines already have the prefix, remove it.
 * Otherwise, prepend each line with the prefix.
 *
 * Special case: when the prefix is a heading (`# `, `## `, `### `), any *existing*
 * heading prefix is stripped first before the new one is applied, so you cannot
 * end up with stacked headings like `## # text`.
 */
function rawPrependLines(
  textarea: HTMLTextAreaElement,
  value: string,
  onChange: (v: string) => void,
  prefix: string,
  numbered = false
) {
  let start = textarea.selectionStart;
  let end = textarea.selectionEnd;

  // Expand to full line boundaries
  while (start > 0 && value[start - 1] !== '\n') start--;
  while (end < value.length && value[end] !== '\n') end++;

  const selectedLines = value.substring(start, end).split('\n');
  const isHeadingPrefix = !numbered && /^#{1,6} $/.test(prefix);
  const isBulletPrefix = !numbered && /^[-*+] $/.test(prefix);

  let transformed: string;

  if (isHeadingPrefix) {
    // Strip any existing heading prefix first, then check toggle state
    const strippedLines = selectedLines.map(line => line.replace(/^#{1,6} /, ''));
    // Toggle off if all lines already have exactly this heading
    const allExact = selectedLines.every(line => line.startsWith(prefix) && !line.slice(prefix.length).startsWith('#'));
    transformed = allExact
      ? strippedLines.join('\n')
      : strippedLines.map(line => prefix + line).join('\n');
  } else if (numbered) {
    // Ordered list: strip any bullet prefix before numbering
    const stripped = selectedLines.map(line => line.replace(/^[-*+] /, ''));
    const allOrdered = selectedLines.every(line => /^\d+\.\s/.test(line));
    transformed = allOrdered
      ? selectedLines.map(line => line.replace(/^\d+\.\s/, '')).join('\n')
      : stripped.map((line, i) => `${i + 1}. ${line}`).join('\n');
  } else if (isBulletPrefix) {
    // Bullet list: strip any ordered prefix before bulleting
    const stripped = selectedLines.map(line => line.replace(/^\d+\.\s/, ''));
    const allBullet = selectedLines.every(line => line.startsWith(prefix));
    transformed = allBullet
      ? selectedLines.map(line => line.slice(prefix.length)).join('\n')
      : stripped.map(line => prefix + line).join('\n');
  } else {
    // Check if ALL lines already have the prefix → toggle off
    const allHavePrefix = selectedLines.every((line) => line.startsWith(prefix));
    transformed = allHavePrefix
      ? selectedLines.map(line => line.startsWith(prefix) ? line.slice(prefix.length) : line).join('\n')
      : selectedLines.map(line => prefix + line).join('\n');
  }

  const newValue = value.substring(0, start) + transformed + value.substring(end);
  onChange(newValue);
  requestAnimationFrame(() => {
    textarea.selectionStart = start;
    textarea.selectionEnd = start + transformed.length;
    textarea.focus();
  });
}

// ─── Main component ─────────────────────────────────────────────────────────

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  editor,
  onOpenDialog,
  onInsertRaw,
  onInsertAsset,
  isRawMode,
  isPreviewMode,
  onTogglePreview,
  readOnly = false,
  rawTextareaRef,
  rawValue,
  onRawChange,
  rawCursorPos = 0,
  rawSelEnd,
  onRawUndo,
  onRawRedo,
  canRawUndo = false,
  canRawRedo = false,
  toolbarVariant = 'full',
}) => {
  const isMinimal = toolbarVariant === 'minimal';
  const isBasic = toolbarVariant === 'basic';
  // When readOnly, treat everything as if in preview mode for editing purposes
  const editingDisabled = isPreviewMode || readOnly;

  const rawActive = isRawMode ? computeRawActiveStates(rawValue, rawCursorPos, rawSelEnd ?? rawCursorPos) : null;
  const [showInsertMenu, setShowInsertMenu] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const insertMenuRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (insertMenuRef.current && !insertMenuRef.current.contains(e.target as Node)) {
        setShowInsertMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showLinkInput && linkInputRef.current) {
      linkInputRef.current.focus();
    }
  }, [showLinkInput]);

  if (!editor) return null;

  // ─── Read-only: show only the Raw / Preview toggle ───────────────
  if (readOnly) {
    return (
      <div className="border-b border-gray-200 bg-gray-50 px-2 py-1 flex items-center justify-end">
        <div className="inline-flex border border-gray-300 overflow-hidden">
          <button
            type="button"
            onClick={() => { if (isPreviewMode) onTogglePreview(); }}
            className={`inline-flex items-center px-2.5 py-1 text-xs font-medium transition-colors ${!isPreviewMode ? 'bg-indigo-600 text-white cursor-default' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer'}`}
          >
            <Code2 className="h-3.5 w-3.5 mr-1" />
            Raw
          </button>
          <button
            type="button"
            onClick={() => { if (!isPreviewMode) onTogglePreview(); }}
            className={`inline-flex items-center px-2.5 py-1 text-xs font-medium border-l border-gray-300 transition-colors ${isPreviewMode ? 'bg-indigo-600 text-white cursor-default' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer'}`}
          >
            <Eye className="h-3.5 w-3.5 mr-1" />
            Preview
          </button>
        </div>
      </div>
    );
  }

  // ─── Raw-mode toolbar actions ─────────────────────────────────────
  const ta = rawTextareaRef.current;

  const rawBold = () => ta && rawToggleAsteriskWrap(ta, rawValue, onRawChange, 2, 'bold text');
  const rawItalic = () => ta && rawToggleAsteriskWrap(ta, rawValue, onRawChange, 1, 'italic text');
  const rawStrike = () => ta && rawWrapSelection(ta, rawValue, onRawChange, '~~', '~~', 'strikethrough');
  const rawH1 = () => ta && rawPrependLines(ta, rawValue, onRawChange, '# ');
  const rawH2 = () => ta && rawPrependLines(ta, rawValue, onRawChange, '## ');
  const rawH3 = () => ta && rawPrependLines(ta, rawValue, onRawChange, '### ');
  const rawBullet = () => ta && rawPrependLines(ta, rawValue, onRawChange, '- ');
  const rawOrdered = () => ta && rawPrependLines(ta, rawValue, onRawChange, '', true);
  const rawBlockquote = () => ta && rawPrependLines(ta, rawValue, onRawChange, '> ');
  const rawCodeBlock = () => ta && rawWrapSelection(ta, rawValue, onRawChange, '```\n', '\n```', 'code');
  const rawLink = () => {
    const url = linkUrl || 'https://...';
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const selected = rawValue.substring(start, end) || 'link text';
      const insertion = `[${selected}](${url})`;
      const newValue = rawValue.substring(0, start) + insertion + rawValue.substring(end);
      onRawChange(newValue);
      requestAnimationFrame(() => {
        ta.selectionStart = start;
        ta.selectionEnd = start + insertion.length;
        ta.focus();
      });
    }
    setShowLinkInput(false);
    setLinkUrl('');
  };

  // ─── Rich-mode toolbar actions ────────────────────────────────────
  const handleSetLink = () => {
    if (isRawMode) {
      rawLink();
      return;
    }
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const handleAddImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      onInsertRaw(`![image](${url})`);
    }
  };

  // Helper to dispatch raw or rich action
  const act = (rawAction: () => void, richAction: () => void) => {
    if (isRawMode) rawAction();
    else richAction();
  };

  // Insert menu items
  const insertMenuItems: {
    key: string;
    label: string;
    icon: React.ReactNode;
    description: string;
    onClick: () => void;
  }[] = [
      {
        key: 'starRating',
        label: 'Star Rating',
        icon: <Star className="h-4 w-4" />,
        description: 'Insert ⭐ emoji stars (1-5)',
        onClick: () => onOpenDialog('starRating'),
      },
      {
        key: 'cta',
        label: 'CTA Button',
        icon: <MousePointerClick className="h-4 w-4" />,
        description: 'Generic, Full, or Interactive Flow CTA',
        onClick: () => onOpenDialog('cta'),
      },
      {
        key: 'logo',
        label: 'Logo',
        icon: <ImageIcon className="h-4 w-4" />,
        description: 'Circle, square, or original logo',
        onClick: () => onOpenDialog('logo'),
      },
      {
        key: 'mediaCarousel',
        label: 'Media Carousel',
        icon: <GalleryHorizontal className="h-4 w-4" />,
        description: 'Carousel of images/videos',
        onClick: () => onOpenDialog('mediaCarousel'),
      },
      {
        key: 'itemCarousel',
        label: 'Item Carousel',
        icon: <GalleryHorizontal className="h-4 w-4" />,
        description: 'Carousel of text/HTML items',
        onClick: () => onOpenDialog('itemCarousel'),
      },
      {
        key: 'form',
        label: 'Form',
        icon: <FileText className="h-4 w-4" />,
        description: 'Multi-field dynamic form',
        onClick: () => onOpenDialog('form'),
      },
      {
        key: 'singleForm',
        label: 'Single Form',
        icon: <FileText className="h-4 w-4" />,
        description: 'Inline single-field form',
        onClick: () => onOpenDialog('singleForm'),
      },
      {
        key: 'interactiveFlow',
        label: 'Interactive Flow',
        icon: <Workflow className="h-4 w-4" />,
        description: 'Embed a conversation flow',
        onClick: () => onOpenDialog('interactiveFlow'),
      },
      {
        key: 'googleReviews',
        label: 'Google Reviews',
        icon: <MessageSquare className="h-4 w-4" />,
        description: 'Display Google reviews',
        onClick: () => onOpenDialog('googleReviews'),
      },
      {
        key: 'table',
        label: 'Table',
        icon: <Table2 className="h-4 w-4" />,
        description: 'Insert a markdown table',
        onClick: () => onOpenDialog('table'),
      },
      {
        key: 'hr',
        label: 'Horizontal Rule',
        icon: <Minus className="h-4 w-4" />,
        description: 'Insert a divider line',
        onClick: () => onInsertRaw('\n\n---\n\n'),
      },
      {
        key: 'center',
        label: 'Center Block',
        icon: <AlignCenter className="h-4 w-4" />,
        description: 'Wrap selected text in <center>',
        onClick: () => {
          if (isRawMode && ta) {
            rawWrapSelection(ta, rawValue, onRawChange, '<center>', '</center>', 'content here');
          } else {
            const { from, to } = editor.state.selection;
            const selected = editor.state.doc.textBetween(from, to, '\n');
            onInsertRaw(`<center>${selected || 'content here'}</center>`);
          }
        },
      },
    ];

  if (onInsertAsset) {
    insertMenuItems.push({
      key: 'asset',
      label: 'Asset',
      icon: <Image className="h-4 w-4" />,
      description: 'Insert from asset library',
      onClick: onInsertAsset,
    });
  }

  return (
    <div className="border-b border-gray-200 bg-gray-50 px-2 py-1 flex flex-wrap items-center gap-0.5">
      {/* Undo / Redo — hidden in preview, readOnly, and minimal modes */}
      {!isPreviewMode && !readOnly && !isMinimal && (
        <>
          <ToolbarButton
            onClick={() => isRawMode ? onRawUndo?.() : editor.chain().focus().undo().run()}
            disabled={isRawMode ? !canRawUndo : !editor.can().undo()}
            title="Undo (Ctrl+Z)"
          >
            <Undo className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => isRawMode ? onRawRedo?.() : editor.chain().focus().redo().run()}
            disabled={isRawMode ? !canRawRedo : !editor.can().redo()}
            title="Redo (Ctrl+Y)"
          >
            <Redo className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarDivider />
        </>
      )}

      {/* Text Formatting */}
      <ToolbarButton
        onClick={() => act(rawBold, () => editor.chain().focus().toggleBold().run())}
        isActive={isRawMode ? (rawActive?.bold ?? false) : editor.isActive('bold')}
        disabled={editingDisabled}
        title="Bold (**)"
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => act(rawItalic, () => editor.chain().focus().toggleItalic().run())}
        isActive={isRawMode ? (rawActive?.italic ?? false) : editor.isActive('italic')}
        disabled={editingDisabled}
        title="Italic (*)"
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => act(rawStrike, () => editor.chain().focus().toggleStrike().run())}
        isActive={isRawMode ? (rawActive?.strike ?? false) : editor.isActive('strike')}
        disabled={editingDisabled}
        title="Strikethrough (~~)"
      >
        <Strikethrough className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Headings */}
      <ToolbarButton
        onClick={() => act(rawH1, () => editor.chain().focus().toggleHeading({ level: 1 }).run())}
        isActive={isRawMode ? (rawActive?.h1 ?? false) : editor.isActive('heading', { level: 1 })}
        disabled={editingDisabled}
        title="Heading 1 (#)"
      >
        <Heading1 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => act(rawH2, () => editor.chain().focus().toggleHeading({ level: 2 }).run())}
        isActive={isRawMode ? (rawActive?.h2 ?? false) : editor.isActive('heading', { level: 2 })}
        disabled={editingDisabled}
        title="Heading 2 (##)"
      >
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => act(rawH3, () => editor.chain().focus().toggleHeading({ level: 3 }).run())}
        isActive={isRawMode ? (rawActive?.h3 ?? false) : editor.isActive('heading', { level: 3 })}
        disabled={editingDisabled}
        title="Heading 3 (###)"
      >
        <Heading3 className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Lists — hidden in minimal mode */}
      {!isMinimal && (
        <>
          <ToolbarButton
            onClick={() => act(rawBullet, () => editor.chain().focus().toggleBulletList().run())}
            isActive={isRawMode ? (rawActive?.bulletList ?? false) : editor.isActive('bulletList')}
            disabled={editingDisabled}
            title="Bullet List (-)"
          >
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => act(rawOrdered, () => editor.chain().focus().toggleOrderedList().run())}
            isActive={isRawMode ? (rawActive?.orderedList ?? false) : editor.isActive('orderedList')}
            disabled={editingDisabled}
            title="Ordered List (1.)"
          >
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarDivider />
        </>
      )}

      {/* Block — hidden in minimal mode */}
      {!isMinimal && (
        <>
          <ToolbarButton
            onClick={() => act(rawBlockquote, () => editor.chain().focus().toggleBlockquote().run())}
            isActive={isRawMode ? (rawActive?.blockquote ?? false) : editor.isActive('blockquote')}
            disabled={editingDisabled}
            title="Blockquote (>)"
          >
            <Quote className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => act(rawCodeBlock, () => editor.chain().focus().toggleCodeBlock().run())}
            isActive={isRawMode ? (rawActive?.codeBlock ?? false) : editor.isActive('codeBlock')}
            disabled={editingDisabled}
            title="Code Block (```)"
          >
            <Code className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarDivider />
        </>
      )}

      {/* Link */}
      <div className="relative">
        <ToolbarButton
          onClick={() => {
            if (!isRawMode && editor.isActive('link')) {
              editor.chain().focus().unsetLink().run();
            } else {
              setShowLinkInput(!showLinkInput);
            }
          }}
          isActive={!isRawMode && editor.isActive('link')}
          disabled={editingDisabled}
          title="Link"
        >
          <Link className="h-4 w-4" />
        </ToolbarButton>
        {showLinkInput && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200  shadow-lg p-2 z-10 flex items-center space-x-2">
            <input
              ref={linkInputRef}
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSetLink()}
              placeholder="https://..."
              className="px-2 py-1 text-sm border border-gray-300  w-48 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button type="button" onClick={handleSetLink} className="px-2 py-1 text-xs font-medium text-white bg-indigo-600  hover:bg-indigo-700">
              Set
            </button>
          </div>
        )}
      </div>

      {/* Image — hidden in basic mode */}
      {!isBasic && (
        <ToolbarButton onClick={handleAddImage} disabled={editingDisabled} title="Insert Image">
          <Image className="h-4 w-4" />
        </ToolbarButton>
      )}

      {/* ─── Insert Widget Components dropdown — hidden in minimal and basic modes ─── */}
      {!isMinimal && !isBasic && (
        <>
          <ToolbarDivider />
          <div className="relative" ref={insertMenuRef}>
            <button
              type="button"
              disabled={editingDisabled}
              onClick={() => !editingDisabled && setShowInsertMenu(!showInsertMenu)}
              className={`inline-flex items-center px-2 py-1 text-xs font-medium border  transition-colors ${editingDisabled
                ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed opacity-40'
                : 'text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100'
                }`}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Insert
              <ChevronDown className="h-3 w-3 ml-1" />
            </button>
            {showInsertMenu && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200  shadow-lg py-1 z-20 min-w-[240px] max-h-[400px] overflow-y-auto">
                {insertMenuItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      item.onClick();
                      setShowInsertMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <span className="text-gray-500">{item.icon}</span>
                    <div>
                      <div className="font-medium text-gray-900">{item.label}</div>
                      <div className="text-xs text-gray-500">{item.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Spacer to push toggle to the right */}
      <div className="flex-grow" />

      {/* ─── Raw / Preview tab-style toggle ─── */}
      <div className="inline-flex  border border-gray-300 overflow-hidden">
        <button
          type="button"
          onClick={() => { if (isPreviewMode) onTogglePreview(); }}
          className={`inline-flex items-center px-2.5 py-1 text-xs font-medium transition-colors ${!isPreviewMode
            ? 'bg-indigo-600 text-white cursor-default'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer'
            }`}
        >
          {isRawMode ? <Code2 className="h-3.5 w-3.5 mr-1" /> : <Pencil className="h-3.5 w-3.5 mr-1" />}
          {isRawMode ? 'Raw' : 'Edit'}
        </button>
        <button
          type="button"
          onClick={() => { if (!isPreviewMode) onTogglePreview(); }}
          className={`inline-flex items-center px-2.5 py-1 text-xs font-medium border-l border-gray-300 transition-colors ${isPreviewMode
            ? 'bg-indigo-600 text-white cursor-default'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer'
            }`}
        >
          <Eye className="h-3.5 w-3.5 mr-1" />
          Preview
        </button>
      </div>
    </div>
  );
};
